/**
 * Tour Engine - Sprint 5, F5.1
 * =============================
 *
 * A lightweight in-house product-tour engine built on top of shadcn's
 * Popover. Tours are JSON-defined: an array of steps, each pointing at a
 * DOM element via `[data-tour="…"]` selector.
 *
 * Architecture:
 *   <TourProvider> - global state, persistence, registration of tours
 *   <Tour active>  - visual popover anchored to the current target step
 *
 * Persistence:
 *   User progress is saved to `user_preferences.tour_progress` via the
 *   /api/tours/progress endpoint. Failures are silent - the engine
 *   degrades gracefully if the backend is unreachable.
 *
 * Adding a tour:
 *   1. Drop a JSON file in `/app/frontend/src/tours/` (see `firstLogin.js`).
 *   2. Register it via `registerTour(def)` (typically inside the page that
 *      should auto-launch it, or in a global init).
 *   3. Call `startTour(tourId)` whenever you want it to run.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../ui/button';
import { X, ArrowRight, ArrowLeft, Sparkles } from 'lucide-react';
import { API, useAuth } from '../../App';
import axios from 'axios';

const TourContext = createContext(null);

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within a TourProvider');
  return ctx;
};

export const TourProvider = ({ children }) => {
  const { user } = useAuth() || {};
  const [tours, setTours] = useState({});               // id → definition
  const [activeTourId, setActiveTourId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState({});         // id → { completed, dismissed, last_step }
  const [enabled, setEnabled] = useState(true);
  const fetchedRef = useRef(false);
  // Live refs to avoid stale-closure issues when register + start happen in
  // the same tick (state batch hasn't flushed yet when setTimeout fires).
  const toursRef = useRef({});
  const progressRef = useRef({});

  // Load persisted progress + preference once the user is authenticated.
  // Firing this on the /auth page (pre-login) generates a spurious 401 +
  // refresh-token 401 in every load, polluting logs and racing the login
  // flow's cookie commit on slower browsers.
  useEffect(() => {
    if (!user || fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/tours/progress`, { timeout: 6000 });
        if (res.data?.progress) {
          setProgress(res.data.progress);
          progressRef.current = res.data.progress;
        }
        if (typeof res.data?.enabled === 'boolean') setEnabled(res.data.enabled);
      } catch (_e) { /* silent */ }
    })();
  }, [user]);

  const persistProgress = useCallback(async (next) => {
    try {
      await axios.put(`${API}/tours/progress`, { progress: next });
    } catch (_e) { /* silent: localStorage isn't critical here */ }
  }, []);

  const registerTour = useCallback((def) => {
    if (!def?.id || !Array.isArray(def?.steps)) return;
    toursRef.current = { ...toursRef.current, [def.id]: def };
    setTours(prev => ({ ...prev, [def.id]: def }));
  }, []);

  const startTour = useCallback((tourId) => {
    if (!enabled) return;
    const def = toursRef.current[tourId] || tours[tourId];
    if (!def) {
      // eslint-disable-next-line no-console
      console.warn(`[Tour] unknown tour id: ${tourId}`);
      return;
    }
    const prog = progressRef.current[tourId] || progress[tourId];
    if (prog?.completed || prog?.dismissed) return;
    // Never start a tour on top of a mandatory confirmation dialog (legal form etc.);
    // the gate re-renders the page when it closes and the tour can start then.
    if (typeof document !== 'undefined' && document.querySelector('[data-testid="legal-form-confirm-gate"], [data-tour-pause]')) return;
    setActiveTourId(tourId);
    setStepIndex(prog?.last_step || 0);
  }, [tours, progress, enabled]);

  const finishCurrent = useCallback((status = 'completed') => {
    if (!activeTourId) return;
    const next = {
      ...progress,
      [activeTourId]: {
        ...progress[activeTourId],
        completed: status === 'completed',
        dismissed: status === 'dismissed',
        last_step: stepIndex,
        updated_at: new Date().toISOString(),
      },
    };
    progressRef.current = next;
    setProgress(next);
    persistProgress(next);
    setActiveTourId(null);
    setStepIndex(0);
  }, [activeTourId, progress, stepIndex, persistProgress]);

  const nextStep = useCallback(() => {
    if (!activeTourId) return;
    const tour = tours[activeTourId];
    if (stepIndex < tour.steps.length - 1) {
      const ns = stepIndex + 1;
      setStepIndex(ns);
      const next = {
        ...progress,
        [activeTourId]: {
          ...progress[activeTourId],
          last_step: ns,
          updated_at: new Date().toISOString(),
        },
      };
      setProgress(next);
      persistProgress(next);
    } else {
      finishCurrent('completed');
    }
  }, [activeTourId, tours, stepIndex, progress, persistProgress, finishCurrent]);

  const prevStep = useCallback(() => {
    setStepIndex(prev => Math.max(0, prev - 1));
  }, []);

  const dismissTour = useCallback(() => finishCurrent('dismissed'), [finishCurrent]);

  const restartTour = useCallback((tourId) => {
    const next = { ...progress, [tourId]: { completed: false, dismissed: false, last_step: 0 } };
    setProgress(next);
    persistProgress(next);
    setActiveTourId(tourId);
    setStepIndex(0);
  }, [progress, persistProgress]);

  const setEnabledPersisted = useCallback(async (v) => {
    setEnabled(v);
    try {
      await axios.put(`${API}/tours/progress`, { enabled: v });
    } catch (_e) { /* silent */ }
  }, []);

  const value = useMemo(() => ({
    tours,
    progress,
    enabled,
    setEnabled: setEnabledPersisted,
    registerTour,
    startTour,
    nextStep,
    prevStep,
    dismissTour,
    restartTour,
    activeTourId,
    stepIndex,
    activeTour: activeTourId ? tours[activeTourId] : null,
    activeStep: activeTourId ? tours[activeTourId]?.steps?.[stepIndex] : null,
  }), [
    tours,
    progress,
    enabled,
    setEnabledPersisted,
    registerTour,
    startTour,
    nextStep,
    prevStep,
    dismissTour,
    restartTour,
    activeTourId,
    stepIndex,
  ]);

  return (
    <TourContext.Provider value={value}>
      {children}
      <TourOverlay />
    </TourContext.Provider>
  );
};

/**
 * TourOverlay - renders the popover for the active step.
 * Anchored to the first DOM element matching `step.target` (CSS selector).
 * Falls back to centered modal if the element isn't on the page.
 */
const TourOverlay = () => {
  const { activeTour, activeStep, stepIndex, nextStep, prevStep, dismissTour } = useTour();
  const [anchor, setAnchor] = useState(null);
  const [gateOpen, setGateOpen] = useState(false);

  // Pause the tour while a mandatory confirmation dialog (legal form) is on screen.
  useEffect(() => {
    if (!activeStep) return undefined;
    const check = () => setGateOpen(Boolean(document.querySelector('[data-testid="legal-form-confirm-gate"], [data-tour-pause]')));
    check();
    const obs = new MutationObserver(check);
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [activeStep]);

  // Resolve anchor element whenever the step changes.
  useEffect(() => {
    if (!activeStep?.target) { setAnchor(null); return; }
    let cancelled = false;
    const lookup = () => {
      try {
        const el = document.querySelector(activeStep.target);
        if (!cancelled) setAnchor(el || null);
      } catch (_e) { setAnchor(null); }
    };
    // Retry a couple of times to allow lazy components to mount.
    lookup();
    const t1 = setTimeout(lookup, 200);
    const t2 = setTimeout(lookup, 600);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
  }, [activeStep]);

  if (!activeTour || !activeStep || gateOpen) return null;

  const total = activeTour.steps.length;
  const isLast = stepIndex === total - 1;
  const isFirst = stepIndex === 0;

  // Calculate popover position based on anchor.
  // iter 949 - Mobile responsiveness: on narrow viewports, stop trying to
  // anchor to the right/left of tiny nav icons (produces off-screen text).
  // Fall back to a bottom-sheet card that spans the visible viewport.
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1024;
  const viewportH = typeof window !== 'undefined' ? window.innerHeight : 800;
  const isMobile = viewportW < 640;
  let style;
  if (isMobile) {
    // Mobile: bottom-sheet card, ~viewport-wide with 12px side gutters.
    const popWidth = Math.max(280, viewportW - 24);
    style = {
      position: 'fixed',
      bottom: 16,
      left: 12,
      width: popWidth,
      maxHeight: viewportH - 32,
      overflowY: 'auto',
      zIndex: 1000,
    };
  } else if (anchor) {
    const rect = anchor.getBoundingClientRect();
    const placement = activeStep.placement || 'bottom';
    const popWidth = Math.min(360, viewportW - 24);
    const margin = 12;
    let top, left;
    switch (placement) {
      case 'right':
        top = rect.top + rect.height / 2 - 80;
        left = rect.right + margin;
        break;
      case 'left':
        top = rect.top + rect.height / 2 - 80;
        left = Math.max(8, rect.left - popWidth - margin);
        break;
      case 'top':
        top = Math.max(8, rect.top - 180);
        left = Math.max(8, rect.left + rect.width / 2 - popWidth / 2);
        break;
      default:
        top = rect.bottom + margin;
        left = Math.max(8, rect.left);
    }
    // Keep on-screen: clamp both edges.
    const maxLeft = viewportW - popWidth - 8;
    left = Math.min(Math.max(8, left), Math.max(8, maxLeft));
    top = Math.max(8, Math.min(top, viewportH - 240));
    style = { position: 'fixed', top, left, width: popWidth, zIndex: 1000 };
  } else {
    // Desktop fallback: centered modal
    const popWidth = Math.min(360, viewportW - 24);
    style = {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: popWidth,
      zIndex: 1000,
    };
  }

  return createPortal(
    <>
      {/* Backdrop highlight - subtle, leaves the page interactive */}
      <div
        className="fixed inset-0 bg-slate-900/30 backdrop-blur-[1px] pointer-events-none"
        style={{ zIndex: 999 }}
        data-testid="tour-backdrop"
      />
      {/* Anchored ring around the target */}
      {anchor && (() => {
        const r = anchor.getBoundingClientRect();
        return (
          <div
            className="fixed border-2 border-[#108981] rounded-xl pointer-events-none shadow-[0_0_0_3px_rgba(16,137,129,0.25)]"
            style={{
              top: r.top - 4,
              left: r.left - 4,
              width: r.width + 8,
              height: r.height + 8,
              zIndex: 999,
            }}
          />
        );
      })()}
      <div
        className="rounded-xl bg-card border border-border shadow-2xl p-5"
        style={style}
        data-testid={`tour-step-${activeTour.id}-${stepIndex}`}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#283F8B] to-[#108981] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">
              {activeTour.title || 'Tour'} · {stepIndex + 1} of {total}
            </span>
          </div>
          <button
            type="button"
            onClick={dismissTour}
            aria-label="Dismiss tour"
            data-testid="tour-dismiss"
            className="text-muted-foreground hover:text-foreground"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="font-semibold text-foreground mb-2" data-testid="tour-step-title">{activeStep.title}</h3>
        <p className="text-sm text-muted-foreground mb-4" data-testid="tour-step-body">{activeStep.body}</p>
        <div className="flex items-center justify-between">
          <div className="flex gap-1">
            {activeTour.steps.map((_s, i) => (
              <span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${i === stepIndex ? 'bg-[#108981]' : 'bg-muted'}`}
              />
            ))}
          </div>
          <div className="flex gap-2">
            {!isFirst && (
              <Button
                size="sm"
                variant="ghost"
                onClick={prevStep}
                data-testid="tour-prev"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                Back
              </Button>
            )}
            <Button
              size="sm"
              onClick={nextStep}
              className="bg-[#108981] hover:bg-[#0a6b65] text-white"
              data-testid="tour-next"
            >
              {isLast ? 'Got it' : 'Next'}
              {!isLast && <ArrowRight className="w-3.5 h-3.5 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TourProvider;
